import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

const useRouterParams = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { push } = useRouter();

  // Add parameters to searchParams and index them if there are existing ones
  const addParam = (name, value) => {
    delete searchParams?.page;
    const existParams = checkExistParams(name);
    if (!existParams) {
      setParam([`${name}[0]`], value);
    } else {
      if (existParams.indexOf(value) !== -1) return;
      setParam(`${name}[${existParams.length}]`, value);
    }
  };
  // set params for params that should be unique
  const setParam = (name, value) => {
    // Convert the value to a string to ensure it handles numeric zeros properly
    const paramValue =
      value !== undefined && value !== null ? String(value) : '';
    push(pathname + '?' + createQueryString(name, paramValue));
  };

  const setParams = (params) => {
    // Get the current search params from the URL
    const searchParams = new URLSearchParams(window.location.search);

    // Loop through the params object and set or delete parameters
    Object.entries(params).forEach(([name, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.set(name, String(value)); // Add or update the parameter
      } else {
        searchParams.delete(name); // Remove the parameter if value is undefined or null
      }
    });

    // Push the updated query string
    push(pathname + '?' + searchParams.toString());
  };
  const createQueryString = useCallback(
    (name, value) => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('page');
      if (!value) {
        params.delete(name);
      } else {
        params.set(name, value);
      }

      return params.toString();
    },
    [searchParams]
  );

  //  clear all params ***************************
  const clearParams = (...params) => {
    if (!params.length) {
      push(pathname);
      return;
    }
    // Clear the given params
    const newQuery = Object.keys(searchParams.entries()).reduce((acc, curr) => {
      if (params.indexOf(curr) === -1) {
        acc[curr] = searchParams.get(curr);
      }
      return acc;
    }, {});

    push(pathname + '?' + newQuery.toString());
  };

  // Remove a parameter and index the rest of them
  const removeParam = (name, value) => {
    // Create a new URLSearchParams object from the existing one
    const newQuery = new URLSearchParams(searchParams);

    // Reset and remove pagination
    newQuery.delete('page');

    // Check how many params with this name exist, e.g., brand[0]="", brand[1]=""
    const existParams = checkExistParams(name);

    // Parameter does not exist, nothing to remove
    if (!existParams || existParams.length === 0) {
      return;
    }

    // Filter out the parameter with the specific value
    const updatedParams = existParams.filter((param) => param !== value);

    // Remove existing params with the specific name
    // Collect keys to be deleted
    const keysToDelete = [];
    for (const [key] of newQuery.entries()) {
      const matches = key.match(new RegExp(`^${name}\\[(\\d+)]$`));
      if (matches) {
        keysToDelete.push(key);
      }
    }
    // Delete collected keys
    keysToDelete.forEach((key) => newQuery.delete(key));

    // Add updated parameters with the specific name again to newQuery
    updatedParams.forEach((param, index) => {
      newQuery.append(`${name}[${index}]`, param);
    });
    // Push the new URL with updated query parameters
    push(pathname + '?' + newQuery.toString());
  };

  // remove attributes params
  const removeAttrParam = (id, value) => {
    const existParams = checkExistattr(id);

    if (!existParams) {
      return; // Parameter does not exist, nothing to remove
    }

    const newQuery = new URLSearchParams(searchParams);

    // Reset and remove pagination
    newQuery.delete('page');

    const updatedParams = existParams.filter((param) => param !== value);

    // Remove existing params with the specific name
    // Collect keys to be deleted
    const keysToDelete = [];
    for (const [key] of newQuery.entries()) {
      const matches = key.match(new RegExp(`^attr\\[${id}\\]\\[(\\d+)]$`));
      if (matches) {
        keysToDelete.push(key);
      }
    }
    // Delete collected keys
    keysToDelete.forEach((key) => newQuery.delete(key));

    updatedParams.forEach((param, index) => {
      newQuery.append(`attr[${id}][${index}]`, param);
    });

    push(pathname + '?' + newQuery.toString());
  };

  // check if params exist
  const checkExistParams = (name) => {
    const existParams = [];
    for (const [key, value] of searchParams.entries()) {
      const matches = key.match(new RegExp(`^${name}\\[(\\d+)]$`));
      if (matches) {
        const index = parseInt(matches[1]);
        existParams[index] = searchParams.get(key);
      }
    }
    return existParams;
  };
  // check if attributes exist
  const checkExistattr = (id) => {
    const existParams = [];
    for (const [key, value] of searchParams.entries()) {
      const matches = key.match(new RegExp(`^attr\\[${id}\\]\\[(\\d+)]$`));
      if (matches) {
        const index = parseInt(matches[1]);
        existParams[index] = value;
      }
    }
    return existParams;
  };

  return {
    addParam,
    setParam,
    setParams,
    clearParams,
    removeParam,
    checkExistParams,
    checkExistattr,
    removeAttrParam,
  };
};
export { useRouterParams };
